/**
 * Copyright (C) 2026 Checkmk GmbH - License: Checkmk Enterprise License
 * This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
 * conditions defined in the file COPYING, which is part of this source code package.
 */
import { type Ref, type ShallowRef, onBeforeUnmount, ref, shallowRef } from 'vue'

import {
  type ConversationSummary,
  ConversationsApi
} from '@/nonfree/ai/control-plane/api/conversations'
import { createControlPlaneClient } from '@/nonfree/ai/control-plane/api/transport'
import { Conversation, describeError } from '@/nonfree/ai/control-plane/conversation'

export interface AiConversations {
  summaries: Ref<ConversationSummary[]>
  conversation: ShallowRef<Conversation>
  error: Ref<string | null>
  refresh: () => Promise<void>
  startNew: () => void
  open: (conversationId: string) => Promise<boolean>
  remove: (conversationId: string) => Promise<void>
}

export default function useAiConversations(): AiConversations {
  const api = new ConversationsApi(createControlPlaneClient())

  const summaries = ref<ConversationSummary[]>([])
  const conversation = shallowRef(new Conversation(api))
  const error = ref<string | null>(null)

  async function refresh(): Promise<void> {
    try {
      summaries.value = await api.list()
      error.value = null
    } catch (e) {
      error.value = describeError(e)
    }
  }

  function switchTo(next: Conversation): void {
    conversation.value.dispose()
    conversation.value = next
  }

  function startNew(): void {
    switchTo(new Conversation(api))
  }

  async function open(conversationId: string): Promise<boolean> {
    try {
      switchTo(Conversation.resume(api, await api.get(conversationId)))
      error.value = null
    } catch (e) {
      error.value = describeError(e)
      return false
    }
    const summary = summaries.value.find((s) => s.conversation_id === conversationId)
    if (summary?.status === 'running') {
      // not awaited, so that the chat shows while the turn runs
      void conversation.value.follow()
    }
    return true
  }

  async function remove(conversationId: string): Promise<void> {
    try {
      await api.delete(conversationId)
      summaries.value = summaries.value.filter((s) => s.conversation_id !== conversationId)
      if (conversation.value.id.value === conversationId) {
        startNew()
      }
      error.value = null
    } catch (e) {
      error.value = describeError(e)
    }
  }

  onBeforeUnmount(() => conversation.value.dispose())

  return { summaries, conversation, error, refresh, startNew, open, remove }
}
